/**
 * SEO templates — the rows every page title, description and H1 is rendered from
 * (docs/05-admin/23 §3.3, backend 40 §4.6).
 *
 * **Override → template → hard failure.** There is no fallback title in code, so a template is
 * the only thing standing between a page and a build that refuses it. The server checks every
 * save — unknown tokens, tokens the page type cannot fill, a pair the taxonomy lacks, outcome
 * claims — and refuses to switch off a template published pages render through. This screen
 * greys out the tokens it would refuse and shows its refusals verbatim; it never decides.
 *
 * ⛔ **No delete.** A template is switched off, never removed, so a page's history always
 * resolves to the row that titled it.
 */
import { useState } from "react";
import { Card, Icon, StatusChip } from "../components/primitives";
import { Input, Select } from "../components/forms";
import { ErrorState, LoadingState } from "../components/feedback";
import { DataTable } from "../components/data";
import { Consequences, REFUSED, ScopeAction } from "../components/kit";
import { useConsole } from "../app/console";
import { errorMessage } from "../lib/api/client";
import {
  useCreateSeoTemplate,
  usePreviewSeoTemplate,
  useSeoTemplates,
  useUpdateSeoTemplate,
  type SeoTemplateIn,
  type SeoTemplateRow,
} from "../lib/api/hooks/seo";

export const ROBOTS = [
  "index,follow",
  "noindex,follow",
  "index,nofollow",
  "noindex,nofollow",
] as const;
const LOCALES = ["en", "hi"] as const;
const FIELDS = ["title", "description", "h1"] as const;
type Field = (typeof FIELDS)[number];

/** The tokens a template uses, in order of first appearance — the preview asks for each. */
export function tokensIn(...templates: string[]): string[] {
  const seen: string[] = [];
  for (const template of templates) {
    for (const match of template.matchAll(/\{([a-z_]+)\}/g)) {
      const token = match[1]!;
      if (!seen.includes(token)) seen.push(token);
    }
  }
  return seen;
}

interface Draft {
  title: string;
  description: string;
  h1: string;
}

function TokenPalette({
  all,
  allowed,
  onInsert,
}: {
  all: readonly string[];
  allowed: readonly string[];
  onInsert: (token: string) => void;
}) {
  return (
    <div
      style={{ display: "flex", flexWrap: "wrap", gap: "var(--sp-xs)" }}
      aria-label="Tokens"
    >
      {all.map((token) => (
        <button
          key={token}
          type="button"
          className="mono"
          disabled={!allowed.includes(token)}
          title={
            allowed.includes(token)
              ? `Insert {${token}}`
              : "This page type has nothing to fill it with"
          }
          onClick={() => onInsert(token)}
          style={{
            border: "1px solid var(--c-border)",
            borderRadius: "var(--r-chip)",
            background: "var(--c-sunk)",
            padding: "2px var(--sp-sm)",
            fontSize: "var(--fs-admin-cell)",
            opacity: allowed.includes(token) ? 1 : 0.4,
          }}
        >
          {`{${token}}`}
        </button>
      ))}
    </div>
  );
}

function DraftFields({
  draft,
  setDraft,
  all,
  allowed,
}: {
  draft: Draft;
  setDraft: (d: Draft) => void;
  all: readonly string[];
  allowed: readonly string[];
}) {
  const [target, setTarget] = useState<Field>("title");
  return (
    <>
      <Input
        density="admin"
        label="Title"
        value={draft.title}
        onChange={(v) => setDraft({ ...draft, title: v })}
      />
      <Input
        density="admin"
        label="Description"
        value={draft.description}
        onChange={(v) => setDraft({ ...draft, description: v })}
        multiline
        helper="What a search result shows under the title. Traditional significance only — never what a puja brings about."
      />
      <Input
        density="admin"
        label="H1"
        value={draft.h1}
        onChange={(v) => setDraft({ ...draft, h1: v })}
      />
      <div
        style={{
          display: "flex",
          gap: "var(--sp-sm)",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Select
          density="admin"
          label="Insert into"
          value={target}
          onChange={(v) => setTarget(v as Field)}
          options={FIELDS}
        />
        <TokenPalette
          all={all}
          allowed={allowed}
          onInsert={(token) =>
            setDraft({ ...draft, [target]: `${draft[target]}{${token}}` })
          }
        />
      </div>
    </>
  );
}

function Preview({ row }: { row: SeoTemplateRow }) {
  const { scope } = useConsole();
  const preview = usePreviewSeoTemplate();
  const needed = tokensIn(
    row.title_template,
    row.description_template,
    row.h1_template,
  ).filter((t) => t !== "month_year");
  const [values, setValues] = useState<Record<string, string>>({});
  return (
    <div style={{ display: "grid", gap: "var(--sp-sm)" }}>
      <strong>Sample preview</strong>
      {needed.map((token) => (
        <Input
          key={token}
          density="admin"
          label={`{${token}}`}
          value={values[token] ?? ""}
          onChange={(v) => setValues({ ...values, [token]: v })}
        />
      ))}
      <div>
        <ScopeAction
          scope={scope}
          need="catalogue_content"
          size="sm"
          variant="secondary"
          disabled={preview.isPending}
          onClick={() => preview.mutate({ id: row.id, values })}
        >
          Preview
        </ScopeAction>
      </div>
      {preview.isError && (
        <Consequences tone="error" kicker={REFUSED} items={[errorMessage(preview.error)]} />
      )}
      {preview.isSuccess && (
        <Card variant="flat" padding="var(--sp-md)">
          <div style={{ color: "var(--c-link, var(--c-ink))", fontSize: 18 }}>
            {preview.data.title}
          </div>
          <div
            style={{ fontSize: "var(--fs-admin-cell)", color: "var(--c-ink2)" }}
          >
            {preview.data.description}
          </div>
          <div
            style={{
              fontSize: "var(--fs-admin-cell)",
              color: "var(--c-ink3)",
              marginTop: "var(--sp-xs)",
            }}
          >
            H1: {preview.data.h1} · title {preview.data.title_length} chars ·
            description {preview.data.description_length} chars
          </div>
        </Card>
      )}
    </div>
  );
}

function Editor({
  row,
  all,
  allowed,
  onDone,
}: {
  row: SeoTemplateRow;
  all: readonly string[];
  allowed: readonly string[];
  onDone: () => void;
}) {
  const { scope, toast } = useConsole();
  const update = useUpdateSeoTemplate();
  const [draft, setDraft] = useState<Draft>({
    title: row.title_template,
    description: row.description_template,
    h1: row.h1_template,
  });
  const [robots, setRobots] = useState(row.robots_default);
  const name = `${row.page_type} · ${row.subtype} · ${row.locale}`;

  const save = () =>
    update.mutate(
      {
        id: row.id,
        patch: {
          title_template: draft.title,
          description_template: draft.description,
          h1_template: draft.h1,
          robots_default: robots,
        },
      },
      {
        onSuccess: () => {
          toast({ tone: "success", message: `${name} saved` });
          onDone();
        },
      },
    );
  const toggle = () =>
    update.mutate(
      { id: row.id, patch: { is_active: !row.is_active } },
      {
        onSuccess: () => {
          toast({
            tone: row.is_active ? "error" : "success",
            message: `${name} ${row.is_active ? "switched off" : "switched on"}`,
          });
          onDone();
        },
      },
    );

  return (
    <Card variant="flat" padding="var(--sp-lg)">
      <div style={{ display: "grid", gap: "var(--sp-md)" }}>
        <strong>{name}</strong>
        <DraftFields
          draft={draft}
          setDraft={setDraft}
          all={all}
          allowed={allowed}
        />
        <Select
          density="admin"
          label="Robots"
          value={robots}
          onChange={setRobots}
          options={ROBOTS}
        />
        {update.isError && (
          <Consequences tone="error" kicker={REFUSED} items={[errorMessage(update.error)]} />
        )}
        <div style={{ display: "flex", gap: "var(--sp-sm)" }}>
          <ScopeAction
            scope={scope}
            need="catalogue_content"
            size="sm"
            disabled={
              !draft.title.trim() ||
              !draft.description.trim() ||
              !draft.h1.trim() ||
              update.isPending
            }
            onClick={save}
          >
            Save
          </ScopeAction>
          <ScopeAction
            scope={scope}
            need="catalogue_content"
            size="sm"
            variant={row.is_active ? "destructive" : "secondary"}
            disabled={update.isPending}
            onClick={toggle}
          >
            {row.is_active ? "Switch off" : "Switch on"}
          </ScopeAction>
          <ScopeAction
            scope={scope}
            need="catalogue_content"
            size="sm"
            variant="secondary"
            onClick={onDone}
          >
            Close
          </ScopeAction>
        </div>
        <Preview
          key={`${row.id}-${row.title_template}-${row.description_template}-${row.h1_template}`}
          row={row}
        />
      </div>
    </Card>
  );
}

function NewTemplate({
  tokens,
  all,
  onDone,
}: {
  tokens: Record<string, string[]>;
  all: readonly string[];
  onDone: () => void;
}) {
  const { scope, toast } = useConsole();
  const create = useCreateSeoTemplate();
  const types = Object.keys(tokens).sort();
  const [pageType, setPageType] = useState(types[0] ?? "");
  const [subtype, setSubtype] = useState("");
  const [locale, setLocale] = useState<string>("en");
  const [robots, setRobots] = useState<string>("index,follow");
  const [draft, setDraft] = useState<Draft>({
    title: "",
    description: "",
    h1: "",
  });

  const submit = () =>
    create.mutate(
      {
        page_type: pageType as SeoTemplateIn["page_type"],
        subtype: subtype.trim(),
        locale: locale as SeoTemplateIn["locale"],
        title_template: draft.title,
        description_template: draft.description,
        h1_template: draft.h1,
        robots_default: robots,
      },
      {
        onSuccess: () => {
          toast({
            tone: "success",
            message: `${pageType} · ${subtype.trim()} · ${locale} added`,
          });
          onDone();
        },
      },
    );

  return (
    <Card variant="flat" padding="var(--sp-lg)">
      <div style={{ display: "grid", gap: "var(--sp-md)" }}>
        <strong>New template</strong>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) 120px",
            gap: "var(--sp-md)",
          }}
        >
          <Select
            density="admin"
            label="Page type"
            value={pageType}
            onChange={setPageType}
            options={types}
          />
          <Input
            density="admin"
            label="Subtype"
            value={subtype}
            onChange={setSubtype}
          />
          <Select
            density="admin"
            label="Locale"
            value={locale}
            onChange={setLocale}
            options={LOCALES}
          />
        </div>
        <DraftFields
          draft={draft}
          setDraft={setDraft}
          all={all}
          allowed={tokens[pageType] ?? []}
        />
        <Select
          density="admin"
          label="Robots"
          value={robots}
          onChange={setRobots}
          options={ROBOTS}
        />
        {create.isError && (
          <Consequences tone="error" kicker={REFUSED} items={[errorMessage(create.error)]} />
        )}
        <div style={{ display: "flex", gap: "var(--sp-sm)" }}>
          <ScopeAction
            scope={scope}
            need="catalogue_content"
            size="sm"
            disabled={
              !subtype.trim() ||
              !draft.title.trim() ||
              !draft.description.trim() ||
              !draft.h1.trim() ||
              create.isPending
            }
            onClick={submit}
          >
            Add template
          </ScopeAction>
          <ScopeAction
            scope={scope}
            need="catalogue_content"
            size="sm"
            variant="secondary"
            onClick={onDone}
          >
            Cancel
          </ScopeAction>
        </div>
      </div>
    </Card>
  );
}

export function SeoTemplates() {
  const { scope } = useConsole();
  const [pageType, setPageType] = useState("");
  const [editing, setEditing] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);

  const list = useSeoTemplates(pageType ? { page_type: pageType } : {});
  const rows = list.data?.items ?? [];
  const tokens = list.data?.tokens ?? {};
  const all = [...new Set(Object.values(tokens).flat())].sort();
  const open = rows.find((r) => r.id === editing) ?? null;

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--sp-sm)",
          background: "var(--c-sunk)",
          border: "1px solid var(--c-border)",
          borderRadius: "var(--r-tile)",
          padding: "var(--sp-md) var(--sp-lg)",
          fontSize: "var(--fs-admin-ui)",
          color: "var(--c-ink2)",
          flexWrap: "wrap",
        }}
      >
        <Icon name="globe" size={16} />
        <span style={{ flex: 1, minWidth: 280 }}>
          A page's own override wins, then its template, and with neither the
          build stops — there is no fallback title in code. A template published
          pages use cannot be switched off.
        </span>
        <Select
          density="admin"
          ariaLabel="Page type"
          value={pageType || "All page types"}
          onChange={(v) => {
            setPageType(v === "All page types" ? "" : v);
            setEditing(null);
          }}
          options={["All page types", ...Object.keys(tokens).sort()]}
          style={{ minWidth: 200 }}
        />
        <ScopeAction
          scope={scope}
          need="catalogue_content"
          size="sm"
          icon="plus"
          disabled={!list.isSuccess}
          onClick={() => {
            setEditing(null);
            setAdding(true);
          }}
        >
          New template
        </ScopeAction>
      </div>

      {list.isPending && <LoadingState message="Reading the templates…" />}
      {list.isError && (
        <ErrorState
          title="Could not read the templates"
          cause={errorMessage(list.error)}
        />
      )}

      {adding && (
        <NewTemplate
          tokens={tokens}
          all={all}
          onDone={() => setAdding(false)}
        />
      )}
      {open && (
        <Editor
          key={open.id}
          row={open}
          all={all}
          allowed={tokens[open.page_type] ?? []}
          onDone={() => setEditing(null)}
        />
      )}

      {list.isSuccess && (
        <DataTable
          density="comfortable"
          onRowClick={(row) => {
            setAdding(false);
            setEditing(Number(row.id));
          }}
          footer={<span>{rows.length} templates</span>}
          columns={[
            { key: "pair", label: "Page type · subtype", mono: true },
            { key: "locale", label: "Locale" },
            { key: "title", label: "Title" },
            { key: "robots", label: "Robots", mono: true },
            { key: "status", label: "Status" },
          ]}
          rows={rows.map((r) => ({
            id: r.id,
            pair: `${r.page_type} · ${r.subtype}`,
            locale: r.locale,
            title: r.title_template,
            robots: r.robots_default,
            status: (
              <StatusChip
                tone={r.is_active ? "success" : "neutral"}
                label={r.is_active ? "Active" : "Off"}
              />
            ),
          }))}
        />
      )}
    </>
  );
}
